/**
 * Skeleton component for loading placeholders
 * Epic 8 Story 8.1.7 - Design System Skeleton Component
 * Follows design system: docs/ui-ux-technical-specification.md
 *
 * @module components/ui/Skeleton
 */

import { HTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Skeleton variant */
  variant?: 'text' | 'paragraph' | 'card' | 'circle';
  /** Number of lines for paragraph variant */
  lines?: number;
}

/**
 * Skeleton loading placeholder component
 * Implements pulse animation (opacity 1 → 0.5 → 1)
 * Used for showing loading states before content appears
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  ({ variant = 'text', lines = 3, className, ...props }, ref) => {
    // Base classes with pulse animation and gray background
    const baseClasses = 'animate-pulse bg-gray-200 rounded';

    // Variant-specific classes
    const variantClasses = {
      text: 'h-4 w-full', // Single line of text
      paragraph: '', // Multiple lines (handled separately)
      card: 'h-48 w-full', // Card placeholder
      circle: 'h-12 w-12 rounded-full', // Avatar/icon placeholder
    };

    // Paragraph variant renders multiple lines
    if (variant === 'paragraph') {
      return (
        <div ref={ref} className={clsx('space-y-2', className)} {...props}>
          {Array.from({ length: lines }).map((_, index) => {
            // Last line is 75% width to simulate natural text ending
            const isLastLine = index === lines - 1;
            return (
              <div
                key={index}
                className={clsx(baseClasses, 'h-4', isLastLine ? 'w-3/4' : 'w-full')}
              />
            );
          })}
        </div>
      );
    }

    // Single skeleton element for other variants
    return (
      <div ref={ref} className={clsx(baseClasses, variantClasses[variant], className)} {...props} />
    );
  }
);

Skeleton.displayName = 'Skeleton';
